<template>
  <div class="room-display" :class="{ single: roomFilter }" @pointerdown="onTouch">

    <!-- Header -->
    <div class="rd-header">
      <span class="rd-logo">🏢</span>
      <span class="rd-title">{{ roomFilter && rooms.length ? roomLabel(rooms[0].email) : t.title }}</span>
      <span class="rd-time">{{ currentTime }}</span>
      <!-- Sprachumschalter: beide Flaggen, die aktive ist hervorgehoben.
           Standard Deutsch, Englisch springt nach LANG_RESET_S zurück -->
      <div class="rd-lang">
        <button
          class="rd-lang-btn"
          :class="{ active: lang === 'de' }"
          aria-label="Deutsch"
          @click="setLang('de')"
        >
          <svg viewBox="0 0 5 3" class="rd-flag" aria-hidden="true">
            <rect width="5" height="1" y="0" fill="#000"/>
            <rect width="5" height="1" y="1" fill="#DD0000"/>
            <rect width="5" height="1" y="2" fill="#FFCE00"/>
          </svg>
        </button>
        <button
          class="rd-lang-btn"
          :class="{ active: lang === 'en' }"
          aria-label="English"
          @click="setLang('en')"
        >
        <svg viewBox="0 0 60 30" class="rd-flag" aria-hidden="true">
          <clipPath id="uj-s"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
          <clipPath id="uj-t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>
          <g clip-path="url(#uj-s)">
            <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
            <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" stroke-width="6"/>
            <path d="M0,0 L60,30 M60,0 L0,30" clip-path="url(#uj-t)" stroke="#C8102E" stroke-width="4"/>
            <path d="M30,0 v30 M0,15 h60" stroke="#fff" stroke-width="10"/>
            <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" stroke-width="6"/>
          </g>
        </svg>
        </button>
      </div>
    </div>

    <!-- Laden -->
    <div v-if="loading" class="rd-loading">
      <div class="rd-spinner"></div>
      <span>{{ t.loading }}</span>
    </div>

    <!-- Fehler -->
    <div v-else-if="error" class="rd-error">
      ⚠️ {{ t.error }}: {{ error }}
    </div>

    <!-- Räume -->
    <div v-else class="rd-rooms" :class="gridClass">
      <div
        v-for="room in rooms"
        :key="room.email"
        class="rd-room"
        :class="room.is_busy ? 'rd-busy' : 'rd-free'"
      >
        <!-- Raumname (im Einzelraum-Modus steht er im Header) -->
        <div v-if="!roomFilter" class="rd-room-name">{{ roomLabel(room.email) }}</div>

        <!-- Status-Badge -->
        <div class="rd-status-badge">
          <span class="rd-status-dot"></span>
          {{ room.is_busy ? t.busy : t.free }}
        </div>

        <!-- Aktueller Termin -->
        <div v-if="room.current" class="rd-current">
          <div class="rd-meeting-time">
            {{ room.current.start }} – {{ room.current.end }}{{ t.timeSuffix }}
          </div>
          <div v-if="room.current.organizer" class="rd-meeting-org">
            👤 {{ room.current.organizer }}
          </div>
          <div class="rd-remaining">
            {{ t.remaining(remaining(room.current.end)) }}
          </div>
        </div>

        <!-- Frei-Anzeige -->
        <div v-else class="rd-free-info">
          <div class="rd-free-icon">✅</div>
          <div class="rd-free-text">{{ t.noMeeting }}</div>
        </div>

        <!-- Nächster Termin -->
        <div class="rd-next" v-if="room.next">
          <span class="rd-next-label">{{ t.next }}</span>
          {{ room.next.start }} – {{ room.next.end }}{{ t.timeSuffix }}
          <span v-if="room.next.organizer" class="rd-next-org">· {{ room.next.organizer }}</span>
        </div>
        <div class="rd-next rd-next-empty" v-else>
          {{ t.noMore }}
        </div>

      </div>
    </div>
  </div>
</template>

<script>
const API        = process.env.VUE_APP_API_URL || 'http://localhost:8010';
const REFRESH_S  = 60; // Sekunden zwischen Abfragen
const LANG_RESET_S = 5; // nach so vielen Sekunden zurück auf Deutsch

// Restzeit-Format je Sprache: Minuten → "2 Std. 15 Min." / "2 h 15 min"
function fmtDuration(left, h, m) {
  if (left < 60) return `${left} ${m}`;
  const hours = Math.floor(left / 60);
  const mins  = left % 60;
  return mins ? `${hours} ${h} ${mins} ${m}` : `${hours} ${h}`;
}

const TEXTS = {
  de: {
    title:      'Meetingräume',
    loading:    'Lade Raumstatus…',
    error:      'Verbindungsfehler',
    free:       'FREI',
    busy:       'BESETZT',
    noMeeting:  'Kein Termin',
    next:       'Nächster Termin:',
    noMore:     'Keine weiteren Termine heute',
    timeSuffix: ' Uhr',
    remaining:  left => `noch ${fmtDuration(left, 'Std.', 'Min.')}`,
  },
  en: {
    title:      'Meeting Rooms',
    loading:    'Loading…',
    error:      'Connection error',
    free:       'AVAILABLE',
    busy:       'OCCUPIED',
    noMeeting:  'No meeting',
    next:       'Next meeting:',
    noMore:     'No more meetings today',
    timeSuffix: '',
    remaining:  left => `${fmtDuration(left, 'h', 'min')} remaining`,
  },
};

export default {
  name: 'RoomDisplay',
  props: {
    kioskKey: { type: String, default: '' },
    roomFilter: { type: String, default: '' }, // Einzelraum, z.B. MR_Verwaltung
  },
  data() {
    return {
      rooms:       [],
      loading:     true,
      error:       null,
      currentTime: '',
      lang:        'de',
      roomNames:   {}, // email → lesbarer Name (aus .env / API)
    };
  },
  mounted() {
    this.load();
    this._clockInterval   = setInterval(this.updateTime, 1000);
    this._refreshInterval = setInterval(this.load, REFRESH_S * 1000);
  },
  beforeUnmount() {
    clearInterval(this._clockInterval);
    clearInterval(this._refreshInterval);
    clearTimeout(this._langTimer);
  },
  computed: {
    t() {
      return TEXTS[this.lang];
    },
    gridClass() {
      const n = this.rooms.length;
      if (n <= 1) return 'cols-1';
      if (n <= 2) return 'cols-2';
      if (n <= 4) return 'cols-2';
      return 'cols-3'; // 5–9 Räume → 3 Spalten
    },
  },
  methods: {
    async load() {
      try {
        let url = `${API}/rooms/status?kiosk_key=${encodeURIComponent(this.kioskKey)}`;
        if (this.roomFilter) url += `&room=${encodeURIComponent(this.roomFilter)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        this.rooms  = data.rooms || [];
        this.error  = null;
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },
    updateTime() {
      this.currentTime = new Date().toLocaleTimeString('de-DE', {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    },
    roomLabel(email) {
      // Nimm den Teil vor dem @, ersetze - und _ durch Leerzeichen, Großbuchstaben
      const name = email.split('@')[0];
      return name.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },
    remaining(endTime) {
      const [h, m] = endTime.split(':').map(Number);
      const now    = new Date();
      const endMin = h * 60 + m;
      const nowMin = now.getHours() * 60 + now.getMinutes();
      return Math.max(0, endMin - nowMin);
    },
    setLang(lang) {
      clearTimeout(this._langTimer);
      this.lang = lang;
      if (lang === 'en') {
        this._langTimer = setTimeout(() => { this.lang = 'de'; }, LANG_RESET_S * 1000);
      }
    },
    onTouch(e) {
      // Wer auf Englisch noch liest und tippt, bekommt wieder volle LANG_RESET_S Sekunden
      if (this.lang !== 'en' || e.target.closest('.rd-lang-btn')) return;
      clearTimeout(this._langTimer);
      this._langTimer = setTimeout(() => { this.lang = 'de'; }, LANG_RESET_S * 1000);
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; margin: 0; padding: 0; }

.room-display {
  min-height: 100vh;
  background: #0f1117;
  color: #fff;
  display: flex;
  flex-direction: column;
  font-family: 'Segoe UI', sans-serif;
  padding: 24px;
  gap: 20px;
}

/* Header */
.rd-header {
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 2px solid #2a2d38;
  padding-bottom: 16px;
}
.rd-logo  { font-size: 28px; }
.rd-title { font-size: 22px; font-weight: 700; flex: 1; }
.rd-time  { font-size: 28px; font-weight: 300; font-variant-numeric: tabular-nums; color: #aaa; }

/* Sprachumschalter */
/* Flaggen etwa so hoch wie die Uhrzeit */
.rd-lang { display: flex; gap: 6px; }
.rd-lang-btn {
  display: flex; align-items: center; justify-content: center;
  width: 44px; height: 30px; padding: 2px;
  background: #1c1f29; border: 2px solid transparent; border-radius: 8px;
  opacity: 0.4;
  cursor: pointer; -webkit-tap-highlight-color: transparent;
  transition: opacity 0.2s, border-color 0.2s;
}
.rd-lang-btn.active { opacity: 1; border-color: #ddd; }
.rd-flag { width: 100%; height: 100%; display: block; border-radius: 2px; }

/* Loading / Error */
.rd-loading, .rd-error {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 16px;
  font-size: 18px; color: #888;
}
.rd-spinner {
  width: 48px; height: 48px;
  border: 4px solid #2a2d38; border-top-color: #ff8a3d;
  border-radius: 50%; animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Räume Grid */
.rd-rooms {
  flex: 1;
  display: grid;
  gap: 20px;
  align-items: stretch;
}
.cols-1 { grid-template-columns: 1fr; }
.cols-2 { grid-template-columns: 1fr 1fr; }
.cols-3 { grid-template-columns: 1fr 1fr 1fr; }
.cols-4 { grid-template-columns: 1fr 1fr 1fr 1fr; }

/* Raum-Karte */
.rd-room {
  border-radius: 20px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border: 3px solid transparent;
  transition: background 0.5s, border-color 0.5s;
}
/* Ganze Karte in kräftiger Statusfarbe, Schrift weiß – auch aus der Entfernung erkennbar */
.rd-free { background: #16a34a; border-color: #22c55e; }
.rd-busy { background: #dc2626; border-color: #ef4444; }

.rd-room-name {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.5px;
}

/* Status-Badge */
.rd-status-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 36px;
  font-weight: 900;
  letter-spacing: 2px;
}
.rd-status-badge { color: #fff; }

.rd-status-dot {
  width: 18px; height: 18px;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}
.rd-status-dot { background: #fff; }
@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.5; transform: scale(1.3); }
}

/* Aktueller Termin */
.rd-current { display: flex; flex-direction: column; gap: 6px; }
.rd-meeting-time  { font-size: 22px; font-weight: 600; color: #fff; }
.rd-meeting-org   { font-size: 18px; color: rgba(255,255,255,0.85); }
.rd-remaining {
  display: inline-block;
  background: rgba(255,255,255,0.2);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  width: fit-content;
}

/* Frei */
.rd-free-info { display: flex; align-items: center; gap: 12px; }
.rd-free-icon { font-size: 36px; }
.rd-free-text { font-size: 20px; color: #fff; font-weight: 600; }

/* Nächster Termin */
.rd-next {
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid rgba(255,255,255,0.35);
  font-size: 13px;
  color: rgba(255,255,255,0.9);
}
.rd-next-label { font-weight: 600; color: #fff; margin-right: 4px; }
.rd-next-org { color: rgba(255,255,255,0.85); }
.rd-next-empty { color: rgba(255,255,255,0.75); }

/* Einzelraum-Modus: große Darstellung für das Display am Raum */
.single .rd-title           { font-size: 32px; }
.single .rd-room            { padding: 48px; gap: 32px; justify-content: center; }
.single .rd-status-badge    { font-size: 96px; }
.single .rd-status-dot      { width: 40px; height: 40px; }
.single .rd-meeting-time    { font-size: 40px; }
.single .rd-meeting-org     { font-size: 32px; }
.single .rd-remaining       { font-size: 24px; padding: 8px 18px; }
.single .rd-free-icon       { font-size: 64px; }
.single .rd-free-text       { font-size: 40px; }
.single .rd-next            { font-size: 24px; margin-top: 0; }
</style>
